import { OidcSecurityService } from 'angular-auth-oidc-client';

import { Inject, Injectable, Injector } from '@angular/core';
import { Apollo, gql } from 'apollo-angular';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import { AppConfiguration, CustomEnum, CustomEnumValue, Group, Mutation, Query, SystemConfig, User, WhoamiResponse } from './api/graphql';
import { LinkOpenMode } from './link-open-mode.enum';


declare var App: any;



@Injectable({
  providedIn: 'root'
})
export class CoreLibService {

  private jwtToken: string;
  private authenticated = false;

  authenticationState = new BehaviorSubject<WhoamiResponse>(null);

  private userState = new BehaviorSubject<User>(null);

  private configuration = new BehaviorSubject<SystemConfig>(null);

  public roleEnum = new BehaviorSubject<CustomEnumValue[]>(null);

  public departmentEnum = new BehaviorSubject<CustomEnumValue[]>(null);

  private countryEnum = new BehaviorSubject<CustomEnumValue[]>(null);

  private salutationEnum = new BehaviorSubject<CustomEnumValue[]>(null);

  APP_SETTING_QUERY = gql`
  query getAppConfiguration{
    getAppConfiguration{
      features
      ios_libVersion
      aos_libVersion
    }
  }`;

  SYSTEM_COFNIG = gql`
    query getSystemConfig{
      getSystemConfig {
        cdnURL
        applicationServerUrl
        portalIcon
      }}`;

  SYSTEM_PROFILE_ENUMS = gql`
    query getSystemEnums($enumIds:[String!]!, $language: String){
      listEnumValues(enumIds : $enumIds, locale : $language ) {
        id,
        keys,
        translations {
          label,
          map
        }
      }}`;

  QUERY_GROUPS = gql`
  query listGroups{
    listGroups{
      id
      name
      memberIds
      roles
      system
      publicGroup
      description
    }}`;


  MUTATION_ENSURE_DIRECT_CHAT = gql`
    mutation ensureDirectCommunicationExists($targetProfileId : ID!){
    ensureDirectCommunicationExists(targetProfileId:$targetProfileId){
      id
      componentId
  }}`;


  constructor(@Inject('jwt') public jwt, private injector: Injector, @Inject('language') private language: string, @Inject('component') private component: string ) {
    let oidSecurityService: OidcSecurityService = null;
    try {
      oidSecurityService = this.injector.get(OidcSecurityService);
    } catch (e) {
      console.info("starting applicatin without oidc support");
    }
    this.jwtToken = jwt;
    if (oidSecurityService != null) {
      oidSecurityService.isAuthenticated$.subscribe((authenticatedState) => {
        if (authenticatedState.isAuthenticated) {
          oidSecurityService.getAccessToken().subscribe((token) => {
            if (authenticatedState.allConfigsAuthenticated[0].configId !== 'external'){
              this.jwtToken = token;
              if (this.jwtToken) {
                this.doAuth().subscribe()
              }
            }
          });
        }
      });
    }
  }

  public getAuth(): Observable<WhoamiResponse> {
    if (this.authenticationState.value != null) {
      return new Observable<WhoamiResponse>((observer) => {
        observer.next(this.authenticationState.value);
        observer.complete();
      });
    }
    return this.doAuth();
  }

  public doAuth(): Observable<WhoamiResponse> {
    const whoAmiFields = 'claims appScope deviceId connectToken expiresAt userProfile { roles{ enumKey enumId } id firstname name realname email phone phoneMobile imageId imageThumbId locked groups{name id system description} } user { username email fullName permissions currentAppId cnumber }';
    const apollo = this.injector.get(Apollo);
    return new Observable((observer) => {
      apollo.query<Query>({
        query: gql`{ whoami {${whoAmiFields}}}`,
      }).subscribe((result) => {
        if (result.errors == null) {
          this.authenticationState.next(result.data.whoami);
          this.userState.next(result.data.whoami.user);
          this.authenticated = true;
          observer.next(result.data.whoami);
          observer.complete();
          this.loadEnums();
        } else {
          this.authenticated = false;
          this.userState.next(null);
          this.authenticationState.next(null);
          observer.error(null);
          observer.complete();
        }
      }, (error: any) => {
        observer.error(error);
      });
    });
  }

  setPageTitle(title: string) {
    if (this.isNativeIOS() && App.setNavigationTitle) {
      App.setNavigationTitle(title).then();
    } else {
      document.title = title;
    }
  }

  openChatWithProfile(profile: string) {
    const apollo = this.injector.get(Apollo);
    apollo.mutate<Mutation>({
      mutation: this.MUTATION_ENSURE_DIRECT_CHAT,
      variables: { targetProfileId: profile }
    }).pipe(map(result => result.data.ensureDirectCommunicationExists)).subscribe((result) => {
      if (result && result.id) {
        this.navigate(result.componentId, { "channelId": result.id }, "ChatChannel");
      }
    });
  }

  downloadFiles(list: { filename: string, contentType: string, url: string }[]) {
    if (typeof App !== 'undefined' && App.downloadFiles) {
      if (this.isNativeAndroid()) {
        App.downloadFiles(JSON.stringify(list));
      } else if (this.isNativeIOS()) {
        App.downloadFiles(list);
      }
    } else {
      for (let i = 0; i < list.length; i++) {
        setTimeout(() => {
          const file = list[i];
          const a = document.createElement('a');
          a.href = file.url;
          a.download = file.url.split('/').pop();
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        }, i * 1000)
      }
    }
  }

  navigate(component: string, parameters: Record<string, string>, componentType?: string) {
    if (typeof App !== 'undefined' && App.navigate) {
      if (this.isNativeAndroid()) {
        App.navigate(component, JSON.stringify(parameters), componentType);
      } else if (this.isNativeIOS()) {
        App.navigate(component, parameters, componentType);
      }
    } else {
      console.error("Navigation not supported");
    }
  }

  shareUrl(url: string, text: string): void {
    const shareData = { url: url, title: text };
    if (navigator && navigator['canShare']) {
      if (navigator.canShare(shareData)) {
        navigator.share(shareData).then();
      } else {
        this.openUrl(url, true);
      }
      return;
    }
    if (this.isNativeIOS() || this.isNativeAndroid()){
      location.href = `share://${url}`;
    }else{
      this.openUrl(url,true);
    }
  }

  /**
   * @deprecated Use @function openLink
   * @param url
   * @param external
   */
  openUrl(url: string, external: boolean): void {
    if (typeof App !== 'undefined' && App.openUrl) {
      App.openUrl(encodeURI(url), external);
    } else {
      if (external) {
        window.open(url, '_blank').focus();
      }
    }
  }

  /**
   *
   * @param url
   * @param mode
   * can be '
   *
   */
  openLink(url: string, mode: LinkOpenMode, title : string): void {
    if (typeof App !== 'undefined' && App.openLink) {
      App.openLink(url, mode, title);
    } else {
      this.openUrl(url, mode === LinkOpenMode.External || mode === LinkOpenMode.Stack)
    }
  }

  getAppConfiguration(): Observable<AppConfiguration> {
    const apollo = this.injector.get(Apollo);
    return apollo.query<Query>({
      query: this.APP_SETTING_QUERY,
      variables: {}
    }).pipe(map(result => result.data.getAppConfiguration));
  }


  isNativeApp(): boolean {
    return this.isNativeIOS() || this.isNativeAndroid();
  }

  getNativeOSVersion(): Promise<number> {
    return new Promise<number>(promise => {
      if (this.isNativeAndroid() && App.version) {
        promise(App.version());
      } else if (this.isNativeIOS() && App.version) {
        App.version().then((result) => {
          promise(result.version);
        });
      } else {
        promise(0);
      }
    });
  }

  isNativeIOS(): boolean {
    if (typeof App !== 'undefined') {
      if (App.isNativeIOS) {
        return App.isNativeIOS();
      }
    }
    return false;
  }


  /**
   * opens push settings
   * @returns
   */
  openPushNotificationSettings(): void{
    if (!this.isNativeApp()){
     return;
    }
    if (this.isNativeIOS() && App.openPushNotificationSettings){
      App.openPushNotificationSettings().then();
    }else if (this.isNativeAndroid() && App.openPushNotificationSettings){
      return App.openPushNotificationSettings();
    }
  }

  /**
   * check if push notifications is enabled on native device
   * @returns
   */
  pushNotificationEnabled(): Promise<boolean> {
    return new Promise<boolean>(promise => {
      if (!this.isNativeApp()){
        promise(false);
        return;
      }
      if (this.isNativeIOS() && App.pushNotificationEnabled){
        App.pushNotificationEnabled().then((result) => {
          promise(result.enabled);
        });
      }else if (this.isNativeAndroid() && App.pushNotificationEnabled){
        promise(App.pushNotificationEnabled());
      }else{
        //Wahrscheinlich wird die Api noch nicht unterstüzt.
        promise(true);
      }
    });
  }


  /**
  * returns the label for the enumKey
  * @param enumKey
  * @returns
  */
  labelForRole(enumKey: string) {
    const role = this.getRoleByKey(enumKey)
    if (role == null) return enumKey;
    return role.value;
  }

  /**
   * return the label for the enumKey
   * @param enumKey
   * @returns
   */
  labelForDepartment(enumKey: string) {
    const department = this.getDepartmentByKey(enumKey)
    if (department == null) return enumKey;
    return department.value;
  }

  isNativeAndroid(): boolean {
    if (typeof App !== 'undefined') {
      if (App.isNativeAndroid) {
        return App.isNativeAndroid();
      }
    }
    return false;
  }

  loadEnums(): void {
    const apollo = this.injector.get(Apollo);
    const enums = ['system-salutation', 'system-countries', 'system-fan-user-role'];
    const customDepartmentsId = `${this.authenticationState.value.appScope}-department`;
    const customRolesId = `${this.authenticationState.value.appScope}-roles`;
    enums.push(customDepartmentsId);
    enums.push(customRolesId);
    apollo.query<Query>({
      query: this.SYSTEM_PROFILE_ENUMS,
      variables: { enumIds: enums, language: this.language }
    }).subscribe((result) => {
      for (const e of result.data.listEnumValues) {
        if (e.id === 'system-salutation') {
          this.salutationEnum.next(this.getEnumValues(e));
        } else if (e.id === 'system-countries') {
          this.countryEnum.next(this.getEnumValues(e));
        } else if (e.id === 'system-fan-user-role' && this.roleEnum.value == null) {
          this.roleEnum.next(this.getEnumValues(e));
        } else if (e.id === customDepartmentsId) {
          this.departmentEnum.next(this.getEnumValues(e));
        } else if (e.id === customRolesId) {
          this.roleEnum.next(this.getEnumValues(e));
        }
      }
    });
  }

  private getEnumValues(customEnum: CustomEnum): CustomEnumValue[] {
    const salutationEnumValues: CustomEnumValue[] = [];
    for (const key of customEnum.keys) {
      if (customEnum.translations && customEnum.translations.length > 0) {
        let label = customEnum.translations[0].map[key]?.value;
        if (label == null) {
          label = key;
        }
        salutationEnumValues.push({
          enumId: customEnum.id,
          value: label,
          enumKey: key
        } as CustomEnumValue);
        salutationEnumValues.sort((a, b) => {
          return a.value.localeCompare(b.value);
        });
      }
    }
    return salutationEnumValues;
  }

  getJWTToken(): string {
    return this.jwtToken;
  }

  updateJWTToken(token: string) {
    this.jwtToken = token;
  }

  isAuthenticated(): boolean {
    return this.authenticated;
  }

  getUserChanges(): Observable<User> {
    return this.userState.asObservable();
  }

  getUser(): User {
    return this.userState.getValue();
  }

  getSecurityInformation(): WhoamiResponse {
    return this.authenticationState.getValue();
  }

  getSecurityInformationAsObservable(): Observable<WhoamiResponse> {
    return this.authenticationState.asObservable();
  }


  hasAnyPermission(permissions: string[]): boolean {
    if (!this.authenticated || !this.authenticationState.getValue()) {
      return false;
    }

    for (const p of permissions) {
      if (this.userState.getValue().permissions.map((f) => f.toString()).includes(p)) {
        return true;
      }
    }

    return false;
  }

  hasAnyClaims(claims: string[]): boolean {
    if (!this.authenticated || !this.authenticationState.getValue()) {
      return false;
    }

    for (const p of claims) {
      if (this.authenticationState.getValue().claims.map((f) => f.toString()).includes(p)) {
        return true;
      }
    }
    return false;
  }

  hasAnyRoles(roles: string[]): boolean {
    if (!this.authenticated || !this.authenticationState.getValue() || !this.authenticationState.getValue().userProfile) {
      return false;
    }
    for (const r of roles) {
      if (this.authenticationState.getValue().userProfile.roles.map((role) => role.enumKey).map((f) => f.toString()).includes(r)) {
        return true;
      }
    }
    return false;
  }

  isInGroup(group: string): boolean {
    if (!this.authenticated || !this.authenticationState.getValue() || !this.authenticationState.getValue().userProfile) {
      return false;
    }
    if (this.authenticationState.getValue().userProfile.groups.map((group) => group.id).indexOf(group) > -1) {
      return true;
    }
    return false;
  }

  getSalutations(): CustomEnumValue[] {
    return this.salutationEnum.getValue();
  }

  getRoleByKey(roleKey: String): CustomEnumValue {
    return this.getRoles().find((role) => role.enumKey === roleKey);
  }

  getDepartmentByKey(departmentKey: String): CustomEnumValue {
    return this.getDepartments().find((department) => department.enumKey === departmentKey);
  }

  getRoles(): CustomEnumValue[] {
    return this.roleEnum.getValue();
  }

  getCountries(): CustomEnumValue[] {
    return this.countryEnum.getValue();
  }

  getDepartments(): CustomEnumValue[] {
    return this.departmentEnum.getValue();
  }

  getGroups(): Observable<Group[]> {
    const apollo = this.injector.get(Apollo)
    return new Observable<Group[]>((observer) => {
      apollo.query<Query>({
        query: this.QUERY_GROUPS,
        variables: {}
      }).subscribe((result) => {
        observer.next(result.data.listGroups);
      });
    });
  }

  getConfig(): Observable<SystemConfig> {
    if (this.configuration.value == null) {
      const apollo = this.injector.get(Apollo)
      apollo.query<Query>({
        query: this.SYSTEM_COFNIG,
        variables: {}
      }).subscribe((result) => {
        this.configuration.next(result.data.getSystemConfig);
      });
    }
    return this.configuration.asObservable();
  }

  inlcudeGlobalStyles(): void {
    const subSec = this.getSecurityInformationAsObservable().subscribe((securityResponse) => {
      if (securityResponse && securityResponse.appScope) {
        const subConfig = this.getConfig().subscribe((value) => {
          if (value && value.cdnURL) {
            const head = document.getElementsByTagName('head')[0];
            const appColorCss = document.createElement('link');
            appColorCss.id = 'app-color.css';
            appColorCss.rel = 'stylesheet';
            appColorCss.href = `${value.cdnURL}/${securityResponse.appScope}/workspace/app-color.css`;
            head.appendChild(appColorCss);

            const stylesCSS = document.createElement('link');
            stylesCSS.id = 'styles.css';
            stylesCSS.rel = 'stylesheet';
            stylesCSS.href = `${value.cdnURL}/${securityResponse.appScope}/workspace/styles.css`;
            head.appendChild(stylesCSS);
            subConfig.unsubscribe();
          }
        });
        subSec.unsubscribe();
      }
    });
  }


  inlcudeGlobalHooks(): Promise<boolean> {
    return new Promise((resolve) => {
      const subSec = this.getSecurityInformationAsObservable().pipe(filter((securityResponse) => !!(securityResponse && securityResponse.appScope)),take(1)).subscribe((securityResponse) => {
        const subConfig = this.getConfig().pipe(filter((value) => !!(value && value.cdnURL)),take(1)).subscribe((value) => {
          const head = document.getElementsByTagName('head')[0];
          const script = document.createElement('script');
          script.type = 'application/javascript';
          script.onload = () => {
            this.hook('hook_load_external_javascript', {component:this.component, profile: securityResponse.userProfile}, null).then((urls) => {
              if (urls){
                for (const url of urls){
                  const scriptJS = document.createElement('script');
                  scriptJS.src = url;
                  scriptJS.type = 'text/javascript';
                  head.appendChild(scriptJS);
                }
              }
            });
            resolve(true);
          }
          script.onerror = () => {
            resolve(false);
          }
          script.src = `${value.cdnURL}/${securityResponse.appScope}/workspace/application-hooks.js`;
          head.appendChild(script);
          subConfig.unsubscribe();
          subSec.unsubscribe();
        });
      });
    });
  }

  hook(name: string, parameters: any, defaultValue: any): Promise<any> {
    return new Promise((resolve) => {
      if (window && window[name]) {
        try {
          const result = window[name](parameters) as Promise<String>;
          result.then(value => {
            resolve(value);
          }).catch(() => {
            resolve(defaultValue);
          });
        } catch (e) {
          resolve(defaultValue);
        }
      } else {
        resolve(defaultValue);
      }
    });
  }
}
